//-----------------------------
// Utilities & Data Access
//-----------------------------

function fetchProductParam(handle, param) {
    const product = window.builderProducts?.[handle];
    if (!product || !(param in product)) return null;
    return product[param];
}

function ensureNumber(value, fallback) {
    const parsed = parseInt(value, 10);
    return Number.isNaN(parsed) ? fallback : parsed;
}

function slugify(value) {
    return String(value || '')
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');
}

function escapeHtml(value) {
    return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

function getBuilderLocale() {
    return (document.documentElement.lang || 'en').split('-')[0];
}

function normalizeLocalizedText(value, fallback = '') {
    if (typeof value === 'string') return value.trim();

    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return fallback;
    }

    const locale = getBuilderLocale();
    const localizedValue = value[locale] ?? value.en ?? value.fr;

    return typeof localizedValue === 'string' ? localizedValue.trim() : fallback;
}

function normalizeProductList(list) {
    if (!Array.isArray(list)) return [];

    return list
        .map((item) => (typeof item === 'string' ? item.trim() : ''))
        .filter(Boolean);
}

/**
 * Splits a product list entry that may contain a variant filter.
 * Format: "handle" or "handle?variant=123456789"
 * Returns { handle, variantId } where variantId is null or the numeric variant ID string.
 */
function parseHandleEntry(entry) {
    const match = entry.match(/^([^?]+)\?variant=(\d+)$/);
    if (!match) return { handle: entry, variantId: null };
    return { handle: match[1].trim(), variantId: match[2] };
}

function formatPrice(cents) {
    const moneyFormat = window.moneyFormat || '${{amount}}';
    const amount = cents / 100;
    const parts = amount.toFixed(2).split('.');
    const intPart = parts[0];
    const decimalPart = parts[1];

    // amount: 1,234.56
    const amountDot = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',') + '.' + decimalPart;
    // amount_with_comma_separator: 1.234,56
    const amountComma = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.') + ',' + decimalPart;
    // amount_with_apostrophe_separator: 1'234.56
    const amountApostrophe = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, "'") + '.' + decimalPart;
    // amount_no_decimals: 1,234
    const amountNoDec = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    // amount_no_decimals_with_comma_separator: 1.234
    const amountNoDecComma = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    // amount_no_decimals_with_space_separator: 1 234
    const amountNoDecSpace = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    // amount_with_space_separator: 1 234,56
    const amountSpace = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ') + ',' + decimalPart;

    return moneyFormat
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amountComma)
        .replace(/\{\{\s*amount_with_apostrophe_separator\s*\}\}/g, amountApostrophe)
        .replace(/\{\{\s*amount_with_space_separator\s*\}\}/g, amountSpace)
        .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, amountNoDecComma)
        .replace(/\{\{\s*amount_no_decimals_with_space_separator\s*\}\}/g, amountNoDecSpace)
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, amountNoDec)
        .replace(/\{\{\s*amount\s*\}\}/g, amountDot);
}

function getAvailableProduct(handle) {
    const product = window.builderProducts?.[handle];
    if (!product) return null;
    if (!isBuilderPurchasable(product)) return null;
    return product;
}

function getProductVariant(product, variantId) {
    if (!product || !Array.isArray(product.variants) || !product.variants.length) return null;

    if (variantId) {
        const selectedVariant = product.variants.find((variant) => String(variant.id) === String(variantId));
        if (selectedVariant) return selectedVariant;
    }

    return product.variants.find((variant) => String(variant.id) === String(product.variant_id)) || product.variants[0] || null;
}

function isVariantPurchasable(variant) {
    return !!variant && (variant.available || (variant.inventory_policy === 'continue' && variant.inventory_quantity <= 0));
}

function getProductBaseTitle(product) {
    if (!product) return '';
    if (product.short_title) return product.short_title;

    const defaultVariant = getProductVariant(product, product.variant_id);
    const defaultLabel = defaultVariant?.option1 || defaultVariant?.title;
    if (!defaultLabel || !product.title) return product.title || '';

    const escapedLabel = defaultLabel.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return product.title.replace(new RegExp(`\\s*[–-]\\s*${escapedLabel}$`), '');
}

function buildProductUrl(product, variantId) {
    const baseUrl = product?.url || (product?.handle ? `/products/${product.handle}` : '#');
    if (!variantId || !baseUrl || baseUrl === '#') return baseUrl;

    const separator = baseUrl.includes('?') ? '&' : '?';
    return `${baseUrl}${separator}variant=${encodeURIComponent(variantId)}`;
}

function isPreorder(product) {
    return product && product.inventory_policy === 'continue' && product.inventory_quantity <= 0;
}

function isBuilderPurchasable(product) {
    return !!product && (product.available || isPreorder(product));
}

function countAvailableProducts(handles) {
    return normalizeProductList(handles).reduce((count, entry) => {
        const parsed = parseHandleEntry(entry);
        return count + (getAvailableProduct(parsed.handle) ? 1 : 0);
    }, 0);
}

function getInputGroupName(stepIndex, substepIndex) {
    return `step-${stepIndex}-substep-${substepIndex}`;
}

function getSummaryKey(stepIndex, substepIndex) {
    return `${stepIndex}-${substepIndex}`;
}

function getNoOptionInputId(stepIndex, substepIndex) {
    return `none-${stepIndex}-${substepIndex}`;
}

function getStepLabel() {
    return typeof stepLabel !== 'undefined' && stepLabel ? stepLabel : 'Step';
}

function getRequiredLabel() {
    return typeof requiredLabel !== 'undefined' && requiredLabel ? requiredLabel : 'Required';
}

function getOptionalLabel() {
    return typeof optionalLabel !== 'undefined' && optionalLabel ? optionalLabel : 'Optional';
}

function getUnavailableLabel() {
    return typeof unavailableLabel !== 'undefined' && unavailableLabel
        ? unavailableLabel
        : 'No products available in this category.';
}

function getViewProductLabel() {
    return typeof viewProductLabel !== 'undefined' && viewProductLabel ? viewProductLabel : 'View product';
}

function getPreorderLabel() {
    return typeof preorderLabel !== 'undefined' && preorderLabel ? preorderLabel : 'Précommande';
}

function getStepsConfiguredTemplate() {
    return typeof stepsConfiguredLabel !== 'undefined' && stepsConfiguredLabel
        ? stepsConfiguredLabel
    : '[configured] of [total] steps configured';
}

function formatConfiguredLabel(configured, total) {
    return getStepsConfiguredTemplate()
        .replace('[configured]', String(configured))
        .replace('[total]', String(total));
}

const BUILDER_STATE = {
    structure: null,
    substepLookup: new Map(),
};

//-----------------------------
// Selection Persistence
//-----------------------------

function getStorageKey() {
    const mainHandle = window.builderProducts?.main?.handle;
    return mainHandle ? `builder-selections-${mainHandle}` : null;
}

/**
 * Restores a variant selection on a product card after localStorage restore.
 */
function applyVariantRestore(input, variantId) {
    input.dataset.variantId = variantId;
    const card = input.closest('.builder-product-card');
    if (!card) return;
    const pill = card.querySelector(`.builder-variant-pill[data-vid="${variantId}"]`);
    if (!pill) return;
    card.querySelectorAll('.builder-variant-pill').forEach((p) => p.classList.remove('-active'));
    pill.classList.add('-active');

    // Update price display
    const priceRow = card.querySelector('.builder-product-card__price-row');
    if (priceRow) {
        const priceEl = priceRow.querySelector('.price');
        const compareEl = priceRow.querySelector('.compare');
        if (priceEl) priceEl.textContent = formatPrice(pill.dataset.vprice);
        if (pill.dataset.vcompare && pill.dataset.vcompare !== pill.dataset.vprice) {
            if (compareEl) {
                compareEl.textContent = formatPrice(pill.dataset.vcompare);
                compareEl.hidden = false;
            }
        } else if (compareEl) {
            compareEl.hidden = true;
        }
    }

    // Update image if variant-specific
    if (pill.dataset.vimage) {
        const img = card.querySelector('figure img');
        if (img) img.src = pill.dataset.vimage;
    }
}

function saveSelections() {
    const key = getStorageKey();
    if (!key) return;

    try {
        const selections = {};

        // Save step/substep radio selections
        document.querySelectorAll('.pool-builder .builder-product-input:checked').forEach((input) => {
            const summaryKey = input.dataset.summaryKey;
            if (!summaryKey) return;

            if (input.dataset.noProduct === 'true') {
                selections[summaryKey] = { noProduct: true };
            } else if (input.type === 'checkbox') {
                if (!selections[summaryKey] || !selections[summaryKey].handles) {
                    selections[summaryKey] = { handles: [], variantIds: {} };
                }
                selections[summaryKey].handles.push(input.dataset.productHandle);
                if (input.dataset.variantId) {
                    selections[summaryKey].variantIds[input.dataset.productHandle] = input.dataset.variantId;
                }
            } else {
                selections[summaryKey] = { handle: input.dataset.productHandle };
                if (input.dataset.variantId) {
                    selections[summaryKey].variantId = input.dataset.variantId;
                }
            }
        });

        // Save promo checkbox states (both checked and unchecked)
        const promoStates = {};
        document.querySelectorAll('.builder-promo-input').forEach((input) => {
            const handle = input.dataset.productHandle;
            if (handle) promoStates[handle] = input.checked;
        });

        const data = { selections, promoStates, timestamp: Date.now() };
        localStorage.setItem(key, JSON.stringify(data));
    } catch (e) {
        // Storage full or unavailable — silently ignore
    }
}

function restoreSelections() {
    const key = getStorageKey();
    if (!key) return;

    try {
        const raw = localStorage.getItem(key);
        if (!raw) return;

        const data = JSON.parse(raw);
        if (!data || typeof data !== 'object') return;

        // Expire after 30 days
        if (data.timestamp && Date.now() - data.timestamp > 30 * 24 * 60 * 60 * 1000) {
            localStorage.removeItem(key);
            return;
        }

        const selections = data.selections || {};
        const promoStates = data.promoStates || {};

        // Restore step/substep radio selections
        Object.entries(selections).forEach(([summaryKey, value]) => {
            const inputs = document.querySelectorAll(`.pool-builder .builder-product-input[data-summary-key="${CSS.escape(summaryKey)}"]`);

            inputs.forEach((input) => {
                if (value.noProduct && input.dataset.noProduct === 'true') {
                    input.checked = true;
                } else if (value.handles && value.handles.includes(input.dataset.productHandle)) {
                    input.checked = true;
                    // Restore variant selection for multiselect
                    if (value.variantIds && value.variantIds[input.dataset.productHandle]) {
                        applyVariantRestore(input, value.variantIds[input.dataset.productHandle]);
                    }
                } else if (value.handle && input.dataset.productHandle === value.handle) {
                    input.checked = true;
                    // Restore variant selection
                    if (value.variantId) {
                        applyVariantRestore(input, value.variantId);
                    }
                }
            });
        });

        // Restore promo checkbox states
        document.querySelectorAll('.builder-promo-input').forEach((input) => {
            const handle = input.dataset.productHandle;
            if (handle && handle in promoStates) {
                input.checked = promoStates[handle];
            }
        });
    } catch (e) {
        // Corrupt data — silently ignore
    }
}

//-----------------------------
// Normalization
//-----------------------------

function normalizeCategory(rawCategory, categoryIndex) {
    const title = normalizeLocalizedText(rawCategory?.category_title, '');

    return {
        index: ensureNumber(rawCategory?.index, categoryIndex + 1),
        key: rawCategory?.key || slugify(title || `category-${categoryIndex + 1}`),
        category_title: title,
        product_list: normalizeProductList(rawCategory?.product_list),
    };
}

function normalizeSubstep(rawSubstep, parentStep, substepIndex) {
    const categories = Array.isArray(rawSubstep?.categories)
        ? rawSubstep.categories.map((category, categoryIndex) => normalizeCategory(category, categoryIndex))
        : [];

    const hasCategories = categories.length > 0;
    const parentStepTitle = normalizeLocalizedText(parentStep?.title, '');
    const substepTitle = normalizeLocalizedText(rawSubstep?.title, parentStepTitle);

    return {
        index: ensureNumber(rawSubstep?.index, substepIndex + 1),
        key: rawSubstep?.key || slugify(`${substepTitle}-${substepIndex + 1}`),
        title: substepTitle,
        text: normalizeLocalizedText(rawSubstep?.text, ''),
        help_content: normalizeLocalizedText(rawSubstep?.help_content, ''),
        required: typeof rawSubstep?.required === 'boolean' ? rawSubstep.required : !!parentStep.required,
        free: !!rawSubstep?.free,
        multiselect: !!rawSubstep?.multiselect,
        categories,
        product_list: hasCategories ? [] : normalizeProductList(rawSubstep?.product_list),
    };
}

function normalizeStep(rawStep, stepIndex) {
    const stepTitle = normalizeLocalizedText(rawStep?.title, `${getStepLabel()} ${stepIndex + 1}`);

    const legacySubstep = {
        index: 1,
        key: `legacy-step-${stepIndex + 1}`,
        title: stepTitle,
        text: normalizeLocalizedText(rawStep?.text, ''),
        required: !!rawStep?.required,
        free: !!rawStep?.free,
        categories: Array.isArray(rawStep?.categories) ? rawStep.categories : [],
        product_list: normalizeProductList(rawStep?.product_list),
    };

    const substepsRaw = Array.isArray(rawStep?.substeps) && rawStep.substeps.length
        ? rawStep.substeps
        : [legacySubstep];

    return {
        index: ensureNumber(rawStep?.index, stepIndex + 1),
        key: rawStep?.key || slugify(stepTitle),
        title: stepTitle,
        text: normalizeLocalizedText(rawStep?.text, ''),
        required: !!rawStep?.required,
        substeps: substepsRaw.map((substep, substepIndex) => normalizeSubstep(substep, rawStep || {}, substepIndex)),
    };
}

function normalizePromos(rawPromos, legacyFreebies) {
    // New format: array of {handle, discount, can_unselect}
    if (Array.isArray(rawPromos) && rawPromos.length) {
        return rawPromos
            .filter((p) => p && typeof p === 'object' && typeof p.handle === 'string')
            .map((p) => ({
                handle: p.handle,
                discount: Math.max(0, Math.min(100, ensureNumber(p.discount, 0))),
                can_unselect: !!p.can_unselect,
            }));
    }

    // Backward compat: convert old freebies format
    if (legacyFreebies) {
        const handles = normalizeProductList(
            Array.isArray(legacyFreebies) ? legacyFreebies : legacyFreebies.product_handles
        );
        return handles.map((handle) => ({
            handle,
            discount: 100,
            can_unselect: false,
        }));
    }

    return [];
}

function normalizeBuilderStructure(rawStructure) {
    const steps = Array.isArray(rawStructure?.steps)
        ? rawStructure.steps.map((step, stepIndex) => normalizeStep(step, stepIndex))
        : [];

    const includes = Array.isArray(rawStructure?.includes)
        ? rawStructure.includes
            .map((item) => normalizeLocalizedText(item, ''))
            .filter(Boolean)
        : [];

    return {
        schema_version: ensureNumber(rawStructure?.schema_version, 1),
        steps,
        includes,
        promos: normalizePromos(rawStructure?.promos, rawStructure?.freebies),
    };
}

//-----------------------------
// Price, Summary, Progress
//-----------------------------

function updateTotalPrice() {
    const checkedInputs = document.querySelectorAll('.pool-builder .builder-product-input:checked:not([data-no-product="true"])');

    let totalPrice = 0;
    let totalComparePrice = 0;

    // Add main product
    const mainProduct = window.builderProducts?.main;
    if (mainProduct) {
        const mainPrice = parseInt(mainProduct.price || 0, 10);
        const mainCompare = parseInt(mainProduct.compare_at_price || 0, 10);

        totalPrice += mainPrice;
        totalComparePrice += mainCompare && mainCompare !== mainPrice ? mainCompare : mainPrice;
    }

    // Add selected bundle items
    checkedInputs.forEach((input) => {
        const handle = input.dataset.productHandle;
        const product = window.builderProducts?.[handle];

        if (!isBuilderPurchasable(product)) return;

        const isFree = input.dataset.free === 'true';

        // Use variant-specific price if a variant is selected
        let price = parseInt(product.price || 0, 10);
        let compare = parseInt(product.compare_at_price || 0, 10);
        const variantId = input.dataset.variantId;
        if (product.variants && product.variants.length > 1 && variantId) {
            const variant = product.variants.find((v) => String(v.id) === String(variantId));
            if (variant) {
                price = parseInt(variant.price || 0, 10);
                compare = parseInt(variant.compare_at_price || 0, 10);
            }
        }

        if (isFree) {
            totalComparePrice += compare && compare !== price ? compare : price;
        } else {
            totalPrice += price;
            totalComparePrice += compare && compare !== price ? compare : price;
        }
    });

    // Add selected promo items
    const promoInputs = document.querySelectorAll('.builder-promo-input:checked');
    promoInputs.forEach((input) => {
        const handle = input.dataset.productHandle;
        const product = window.builderProducts?.[handle];
        if (!isBuilderPurchasable(product)) return;

        const isFree = input.dataset.free === 'true';
        const discount = parseInt(input.dataset.discount || 0, 10);
        const price = parseInt(product.price || 0, 10);
        const compare = parseInt(product.compare_at_price || 0, 10);

        if (isFree) {
            totalComparePrice += compare && compare !== price ? compare : price;
        } else if (discount > 0) {
            const discountedPrice = Math.round(price * (1 - discount / 100));
            totalPrice += discountedPrice;
            totalComparePrice += compare && compare !== price ? compare : price;
        } else {
            totalPrice += price;
            totalComparePrice += compare && compare !== price ? compare : price;
        }
    });

    const currentPriceSelectors = ['[data-summary-total]', '.sticky-cta .price__current'];
    const comparePriceSelectors = ['.sticky-cta .price__was'];

    currentPriceSelectors.forEach((selector) => {
        const container = document.querySelector(selector);
        if (container) container.textContent = formatPrice(totalPrice);
    });

    comparePriceSelectors.forEach((selector) => {
        const container = document.querySelector(selector);
        if (!container) return;

        if (totalComparePrice > totalPrice) {
            container.textContent = formatPrice(totalComparePrice);
            container.style.display = '';
        } else {
            container.style.display = 'none';
        }
    });

    // Update summary pricing strip
    const compareEl = document.querySelector('[data-summary-compare]');
    const compareRow = document.querySelector('[data-summary-compare-row]');
    const savingsEl = document.querySelector('[data-summary-savings]');
    const savingsRow = document.querySelector('[data-summary-savings-row]');

    const savings = totalComparePrice - totalPrice;

    if (compareEl) compareEl.textContent = formatPrice(totalComparePrice);
    if (compareRow) compareRow.style.display = totalComparePrice > totalPrice ? '' : 'none';

    if (savingsEl) savingsEl.textContent = formatPrice(savings);
    if (savingsRow) savingsRow.style.display = savings > 0 ? '' : 'none';

    // Check for preorder products among all selected inputs
    let hasPreorder = false;
    checkedInputs.forEach((input) => {
        const handle = input.dataset.productHandle;
        const product = window.builderProducts?.[handle];
        if (product && isPreorder(product)) hasPreorder = true;
    });
    promoInputs.forEach((input) => {
        const handle = input.dataset.productHandle;
        const product = window.builderProducts?.[handle];
        if (product && isPreorder(product)) hasPreorder = true;
    });

    const preorderNotice = document.querySelector('[data-preorder-notice]');
    if (preorderNotice) preorderNotice.style.display = hasPreorder ? '' : 'none';
}

function setStickyTitle() {
    const stickyTitleContainer = document.querySelector('.sticky-cta .title');
    if (!stickyTitleContainer) return;

    if (typeof stickyCTALabel !== 'undefined' && stickyCTALabel) {
        stickyTitleContainer.textContent = stickyCTALabel;
    }
}

function isSubstepConfigured(substepElem) {
    if (!substepElem) return false;

    if (substepElem.dataset.autoskip === 'true') return true;

    if (substepElem.dataset.multiselect === 'true') {
        if (substepElem.dataset.required !== 'true') return true;
        return !!substepElem.querySelector('.builder-product-input:checked:not([data-no-product="true"])');
    }

    const checked = substepElem.querySelector('.builder-product-input:checked');
    if (!checked) return false;

    if (checked.dataset.noProduct === 'true') {
        return substepElem.dataset.required !== 'true';
    }

    return true;
}

function isStepConfigured(stepElem) {
    const substeps = Array.from(stepElem.querySelectorAll('.builder-substep'));
    if (!substeps.length) return false;

    const requiredSubsteps = substeps.filter((substep) => substep.dataset.required === 'true');

    if (requiredSubsteps.length > 0) {
        return requiredSubsteps.every((substep) => isSubstepConfigured(substep));
    }

    // For fully optional steps, we consider a step configured when at least one real product is selected.
    return substeps.some((substep) => {
        const checked = substep.querySelector('.builder-product-input:checked');
        return checked && checked.dataset.noProduct !== 'true';
    });
}

function updateBuilderProgress() {
    const steps = Array.from(document.querySelectorAll('.pool-builder article.step'));
    const progressFill = document.querySelector('[data-builder-progress-fill]');
    const progressLabel = document.querySelector('[data-builder-progress-label]');

    if (!steps.length) {
        if (progressFill) progressFill.style.width = '0%';
        if (progressLabel) progressLabel.textContent = formatConfiguredLabel(0, 0);
        return;
    }

    const configured = steps.reduce((count, stepElem) => {
        return count + (isStepConfigured(stepElem) ? 1 : 0);
    }, 0);

    const total = steps.length;
    const percentage = Math.round((configured / total) * 100);

    if (progressFill) progressFill.style.width = `${percentage}%`;
    if (progressLabel) progressLabel.textContent = formatConfiguredLabel(configured, total);
}

function renderBuilderSummary() {
    const summaryBody = document.querySelector('[data-summary-body]');
    if (!summaryBody) return;

    const selectedInputs = Array.from(
        document.querySelectorAll('.pool-builder .builder-product-input:checked:not([data-no-product="true"])')
    );

    const rows = selectedInputs
        .map((input) => {
            const handle = input.dataset.productHandle;
            const product = window.builderProducts?.[handle];
            if (!product) return null;

            const summaryKey = input.dataset.summaryKey;
            const lookup = BUILDER_STATE.substepLookup.get(summaryKey);
            if (!lookup) return null;

            const isFree = input.dataset.free === 'true';
            const variantId = input.dataset.variantId;
            let selectedPrice = product.price;
            let selectedCompare = product.compare_at_price;
            let productTitle;
            if (product.variants && product.variants.length > 1 && variantId) {
                const variant = product.variants.find((v) => String(v.id) === String(variantId));
                if (variant) {
                    const baseTitle = product.short_title || getProductBaseTitle(product);
                    const rawLabel = variant.option1 || variant.title || '';
                    const variantLabel = rawLabel && rawLabel !== 'Default Title' ? rawLabel : '';
                    productTitle = variantLabel ? `${baseTitle} – ${variantLabel}` : baseTitle;
                    selectedPrice = variant.price;
                    selectedCompare = variant.compare_at_price;
                } else {
                    productTitle = product.short_title || product.title;
                }
            } else {
                productTitle = product.short_title || product.title;
            }

            return {
                summaryKey,
                stepIndex: lookup.stepIndex,
                substepIndex: lookup.substepIndex,
                stepTitle: lookup.stepTitle,
                productTitle,
                price: selectedPrice,
                compareAtPrice: selectedCompare,
                free: isFree,
            };
        })
        .filter(Boolean)
        .sort((a, b) => {
            if (a.stepIndex !== b.stepIndex) return a.stepIndex - b.stepIndex;
            return a.substepIndex - b.substepIndex;
        });

    // Group rows by step
    const grouped = new Map();
    rows.forEach((row) => {
        if (!grouped.has(row.stepIndex)) {
            grouped.set(row.stepIndex, { title: row.stepTitle, rows: [] });
        }
        grouped.get(row.stepIndex).rows.push(row);
    });

    // Keep the initial pool group (rendered by Liquid), rebuild option groups
    const poolGroup = summaryBody.querySelector('.builder-summary__group');
    const existingOptionGroups = summaryBody.querySelectorAll('.builder-summary__group[data-step]');
    existingOptionGroups.forEach((el) => el.remove());

    grouped.forEach((group, stepIndex) => {
        const groupHtml = `
            <div class="builder-summary__group" data-step="${stepIndex}">
                <div class="builder-summary__group-header">${escapeHtml(group.title)}</div>
                <ul>
                    ${group.rows.map((row) => {
                        const hasCompare = row.compareAtPrice && row.compareAtPrice !== row.price;
                        return `
                            <li data-summary-key="${escapeHtml(row.summaryKey)}">
                                <span class="title">${escapeHtml(row.productTitle)}</span>
                                ${!row.free ? `
                                    <span class="prices">
                                        <span class="price">${formatPrice(row.price)}</span>
                                        ${hasCompare ? `<span class="compare">${formatPrice(row.compareAtPrice)}</span>` : ''}
                                    </span>
                                ` : `<span class="prices"><span class="price free">${escapeHtml(freeLabel)}</span></span>`}
                                <button type="button" class="builder-summary__modify" data-goto-substep="${escapeHtml(row.summaryKey)}">
                                    ${escapeHtml(modifyLabel)}
                                </button>
                            </li>
                        `;
                    }).join('')}
                </ul>
            </div>
        `;
        summaryBody.insertAdjacentHTML('beforeend', groupHtml);
    });

    // Add promo rows to summary
    const promoInputs = document.querySelectorAll('.builder-promo-input:checked');
    if (promoInputs.length) {
        let promoRows = '';
        promoInputs.forEach((input) => {
            const handle = input.dataset.productHandle;
            const product = window.builderProducts?.[handle];
            if (!product) return;

            const isFree = input.dataset.free === 'true';
            const discount = parseInt(input.dataset.discount || 0, 10);
            const originalPrice = parseInt(product.price || 0, 10);
            const productTitle = product.short_title || product.title;

            if (isFree) {
                promoRows += `
                    <li>
                        <span class="title">${escapeHtml(productTitle)}</span>
                        <span class="prices"><span class="price free">${escapeHtml(freeLabel)}</span></span>
                    </li>
                `;
            } else if (discount > 0 && discount < 100) {
                const discountedPrice = Math.round(originalPrice * (1 - discount / 100));
                promoRows += `
                    <li>
                        <span class="title">${escapeHtml(productTitle)}</span>
                        <span class="prices">
                            <span class="price">${formatPrice(discountedPrice)}</span>
                            <span class="compare">${formatPrice(originalPrice)}</span>
                        </span>
                    </li>
                `;
            } else {
                const compareAt = parseInt(product.compare_at_price || 0, 10);
                const hasCompare = compareAt > originalPrice;
                promoRows += `
                    <li>
                        <span class="title">${escapeHtml(productTitle)}</span>
                        <span class="prices">
                            <span class="price">${formatPrice(originalPrice)}</span>
                            ${hasCompare ? `<span class="compare">${formatPrice(compareAt)}</span>` : ''}
                        </span>
                    </li>
                `;
            }
        });

        if (promoRows) {
            const promoGroupHtml = `
                <div class="builder-summary__group" data-step="promos">
                    <div class="builder-summary__group-header">${escapeHtml(typeof giftsAndPromosLabel !== 'undefined' ? giftsAndPromosLabel : 'Gifts & Promos')}</div>
                    <ul>${promoRows}</ul>
                </div>
            `;
            summaryBody.insertAdjacentHTML('beforeend', promoGroupHtml);
        }
    }
}

//-----------------------------
// Scroll and Open Helpers
//-----------------------------

function scrollToElement(element) {
    if (!element) return;

    const offsetSelectors = ['store-header', '.sticky-cta', '.cc-header'];
    let totalOffset = 0;

    offsetSelectors.forEach((selector) => {
        const el = document.querySelector(selector);
        if (el) totalOffset += el.offsetHeight;
    });

    const targetTop = element.getBoundingClientRect().top + window.scrollY;
    const scrollTo = targetTop - totalOffset - 12;

    window.scrollTo({
        top: scrollTo,
        behavior: 'smooth',
    });
}

function openStep(stepElem) {
    if (!stepElem) return;
    stepElem.classList.add('-open');
}

/* ── Substep collapse / expand with measured heights ── */

function collapseBody(substepElem) {
    const body = substepElem.querySelector('.builder-substep__body');
    if (!body) { substepElem.classList.remove('-open'); return; }

    // Pin to actual content height so the transition is proportional
    body.style.overflow = 'hidden';
    body.style.maxHeight = body.scrollHeight + 'px';
    body.offsetHeight; // force reflow

    substepElem.classList.remove('-open');
    body.style.maxHeight = '0px';

    body.addEventListener('transitionend', function handler(e) {
        if (e.propertyName !== 'max-height') return;
        body.style.maxHeight = '';
        body.style.overflow = '';
        body.removeEventListener('transitionend', handler);
    });
}

function expandBody(substepElem, callback) {
    const body = substepElem.querySelector('.builder-substep__body');
    if (!body) { substepElem.classList.add('-open'); if (callback) callback(); return; }

    body.style.overflow = 'hidden';
    substepElem.classList.add('-open');
    const targetHeight = body.scrollHeight;
    body.style.maxHeight = targetHeight + 'px';

    body.addEventListener('transitionend', function handler(e) {
        if (e.propertyName !== 'max-height') return;
        body.style.maxHeight = '';
        body.style.overflow = '';
        body.removeEventListener('transitionend', handler);
        if (callback) callback();
    });
}

function openSubstep(substepElem, callback) {
    if (!substepElem) return;

    // Close every other substep across all steps
    document.querySelectorAll('.pool-builder .builder-substep.-open').forEach((substep) => {
        if (substep !== substepElem) collapseBody(substep);
    });

    expandBody(substepElem, callback);
}

function scrollToSubstepHeader(element) {
    if (!element) return;

    const offsetSelectors = ['store-header', '.sticky-cta', '.cc-header'];
    let headerOffset = 0;
    offsetSelectors.forEach((selector) => {
        const el = document.querySelector(selector);
        if (el) headerOffset += el.offsetHeight;
    });

    const header = element.querySelector('.builder-substep__header') || element;
    const headerTop = header.getBoundingClientRect().top + window.scrollY;
    const scrollTo = headerTop - headerOffset - 16;

    window.scrollTo({ top: Math.max(0, scrollTo), behavior: 'smooth' });
}

function toggleSubstep(substepElem) {
    if (!substepElem) return;

    if (substepElem.classList.contains('-open')) {
        collapseBody(substepElem);
    } else {
        openSubstep(substepElem, () => scrollToSubstepHeader(substepElem));
    }
}

function goToSubstep(summaryKey) {
    const escapedKey = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(summaryKey) : summaryKey;
    const substepElem = document.querySelector(`.builder-substep[data-summary-key="${escapedKey}"]`);
    if (!substepElem) return null;

    const stepElem = substepElem.closest('article.step');
    openStep(stepElem);
    openSubstep(substepElem, () => scrollToSubstepHeader(substepElem));

    return substepElem;
}

function openNextSubstep() {
    // No-op: substeps are always visible, no scrolling.
}

function notifySelectionChanged() {
    document.dispatchEvent(new CustomEvent('builder:selectionChanged'));
}

function enforceInitialOpenState() {
    // All visible steps are always open; only the first visible substep of the first visible step starts open
    const steps = document.querySelectorAll('.pool-builder article.step:not(.-no-products)');
    // Close every substep first
    document.querySelectorAll('.pool-builder .builder-substep.-open').forEach((s) => s.classList.remove('-open'));
    steps.forEach((stepElem, i) => {
        stepElem.classList.add('-open');
        if (i === 0) {
            const firstSubstep = stepElem.querySelector('.builder-substep:not(.-no-products)');
            if (firstSubstep) firstSubstep.classList.add('-open');
        }
    });
}

function updateStepCounters() {
    document.querySelectorAll('.pool-builder article.step').forEach((stepElem) => {
        const stepIndex = stepElem.dataset.stepIndex;
        const count = stepElem.querySelectorAll('.builder-product-input:checked:not([data-no-product])').length;
        const counter = stepElem.querySelector('.builder-step__counter');
        if (!counter) return;
        if (count > 0) {
            counter.textContent = `${count} choix`;
            counter.style.display = '';
        } else {
            counter.textContent = '';
            counter.style.display = 'none';
        }
    });
}

function updateSubstepCounters() {
    document.querySelectorAll('.pool-builder .builder-substep').forEach((substepElem) => {
        const label = substepElem.querySelector('.builder-substep__requirement');
        if (!label) return;
        const count = substepElem.querySelectorAll('.builder-product-input:checked:not([data-no-product])').length;
        if (count > 0) {
            label.textContent = `${count} choix`;
            label.classList.add('-has-choices');
        } else {
            label.textContent = label.dataset.defaultLabel || '';
            label.classList.remove('-has-choices');
        }
    });
}

//-----------------------------
// Help Modal
//-----------------------------

function getHelpLabel() {
    const lang = document.documentElement.lang || 'en';
    return lang.startsWith('fr') ? 'Aidez-moi \u00e0 choisir' : 'Help me choose';
}

function renderHelpLink(substep) {
    if (!substep.help_content) return '';

    const modalId = `builder-help-${substep.key}`;
    return ` <button type="button" class="builder-help-link" data-open-help="${modalId}" aria-label="${escapeHtml(getHelpLabel())}">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="8" cy="8" r="7.25" stroke="currentColor" stroke-width="1.5"/><text x="8" y="11.5" text-anchor="middle" font-size="10" font-weight="600" fill="currentColor">?</text></svg>
        <span>${escapeHtml(getHelpLabel())}</span>
    </button>`;
}

function renderHelpModals(builderStructure) {
    // Remove any previously rendered help modals.
    document.querySelectorAll('.builder-help-modal').forEach((el) => el.remove());

    if (!builderStructure?.steps?.length) return;

    builderStructure.steps.forEach((step) => {
        step.substeps.forEach((substep) => {
            if (!substep.help_content) return;

            const modalId = `builder-help-${substep.key}`;
            const modalHtml = `
                <div class="builder-help-modal" id="${modalId}" role="dialog" aria-modal="true" aria-label="${escapeHtml(substep.title)}">
                    <div class="builder-help-modal__backdrop js-close-help"></div>
                    <div class="builder-help-modal__window">
                        <button type="button" class="builder-help-modal__close js-close-help" aria-label="Close">
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 4L4 12M4 4l8 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
                        </button>
                        <h3 class="builder-help-modal__title">${escapeHtml(substep.title)}</h3>
                        <div class="builder-help-modal__content">${substep.help_content}</div>
                    </div>
                </div>
            `;

            document.body.insertAdjacentHTML('beforeend', modalHtml);
        });
    });
}

function openHelpModal(modalId) {
    const modal = document.getElementById(modalId);
    if (!modal) return;

    modal.classList.add('-open');
    document.body.style.overflow = 'hidden';
}

function closeHelpModal(modal) {
    if (!modal) return;

    const container = modal.closest('.builder-help-modal');
    if (!container) return;

    container.classList.remove('-open');
    document.body.style.overflow = '';
}

// Product Preview Modal
function openProductPreview(handle, variantId = null) {
    const product = getAvailableProduct(handle);
    if (!product) return;

    // Remove any existing preview modal
    document.querySelector('.builder-preview-modal')?.remove();

    const selectedVariant = getProductVariant(product, variantId);
    const baseTitle = getProductBaseTitle(product);
    const rawVariantLabel = selectedVariant?.option1 || selectedVariant?.title || '';
    const variantLabel = rawVariantLabel && rawVariantLabel !== 'Default Title' ? rawVariantLabel : '';
    const productTitle = variantLabel ? `${baseTitle} – ${variantLabel}` : (baseTitle || product.title);
    const description = product.description || '';
    const imageUrl = selectedVariant?.featured_image || product.featured_image || '/cdn/shop/files/placeholder.jpg';
    const productUrl = buildProductUrl(product, selectedVariant?.id || variantId);

    const modalHtml = `
        <div class="builder-preview-modal -open" role="dialog" aria-modal="true" aria-label="${escapeHtml(productTitle)}">
            <div class="builder-preview-modal__backdrop js-close-preview"></div>
            <div class="builder-preview-modal__window">
                <button type="button" class="builder-preview-modal__close js-close-preview" aria-label="Close">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 4L4 12M4 4l8 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
                </button>
                <figure class="builder-preview-modal__image">
                    <img src="${imageUrl}" alt="${escapeHtml(productTitle)}">
                </figure>
                <div class="builder-preview-modal__body">
                    <h3 class="builder-preview-modal__title">${escapeHtml(productTitle)}</h3>
                    ${description ? `<p class="builder-preview-modal__description">${escapeHtml(description)}</p>` : ''}
                    <a href="${escapeHtml(productUrl)}" target="_blank" rel="noopener" class="builder-preview-modal__cta">
                        ${escapeHtml(getViewProductLabel())}
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3 11L11 3M11 3H4.5M11 3v6.5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/></svg>
                    </a>
                </div>
            </div>
        </div>
    `;

    document.body.insertAdjacentHTML('beforeend', modalHtml);
    document.body.style.overflow = 'hidden';
}

function closeProductPreview(el) {
    const container = el?.closest('.builder-preview-modal');
    if (!container) return;
    container.remove();
    // Only restore overflow if no other modal is open
    if (!document.querySelector('.builder-help-modal.-open')) {
        document.body.style.overflow = '';
    }
}

//-----------------------------
// Template Renderers
//-----------------------------

function renderStepHeader(step) {
    return `
        <header class="builder-step__header">
            <div class="builder-step__title-wrap">
                <p class="builder-step__eyebrow">${escapeHtml(getStepLabel())} ${step.index}</p>
                <h3 class="builder-step__title">${escapeHtml(step.title)}</h3>
            </div>
            <span class="builder-step__counter" style="display:none"></span>
        </header>
    `;
}

function renderSubstepHeader(substep) {
    const requirementLabel = substep.required ? getRequiredLabel() : getOptionalLabel();

    return `
        <button type="button" class="builder-substep__header">
            <span class="builder-substep__title-wrap">
                <span class="builder-substep__title">${escapeHtml(substep.title)}</span>
            </span>
            <span class="builder-substep__requirement" data-default-label="${escapeHtml(requirementLabel)}">${escapeHtml(requirementLabel)}</span>
            <img class="collapser" src="${collapseImage}" alt="Collapse">
        </button>
    `;
}

function renderNoOptionCard(step, substep, summaryKey, isChecked) {
    const inputId = getNoOptionInputId(step.index, substep.index);
    const groupName = getInputGroupName(step.index, substep.index);
    const inputType = substep.multiselect ? 'checkbox' : 'radio';

    return `
        <li class="builder-product-card builder-product-card--none">
            <input
                class="builder-product-input"
                type="${inputType}"
                id="${inputId}"
                name="${groupName}"
                data-no-product="true"
                data-step-index="${step.index}"
                data-substep-index="${substep.index}"
                data-summary-key="${summaryKey}"
                ${isChecked ? 'checked' : ''}
            >
            <label for="${inputId}">
                <div class="builder-product-card__content">
                    <h4>${escapeHtml(noOptionLabel)}</h4>
                </div>
            </label>
        </li>
    `;
}

function renderProductCard(handle, step, substep, index, summaryKey, filterVariantId) {
    const product = getAvailableProduct(handle);
    if (!product) return '';

    const groupName = getInputGroupName(step.index, substep.index);
    const inputId = `product-${product.id}-${step.index}-${substep.index}-${index}`;
    const hasComparePrice = product.compare_at_price && product.compare_at_price !== product.price;
    const preorderBadge = isPreorder(product) ? `<span class="builder-product-card__preorder">${escapeHtml(getPreorderLabel())}</span>` : '';
    const inputType = substep.multiselect ? 'checkbox' : 'radio';

    // Filter variants when a specific variant ID is provided
    const allVariants = product.variants && product.variants.length > 1 ? product.variants : [];
    const displayVariants = filterVariantId
        ? allVariants.filter((v) => String(v.id) === filterVariantId)
        : allVariants;
    const hasVariants = displayVariants.length > 1;

    // If filtered to a single variant, use its price/image as the card defaults
    const pinnedVariant = filterVariantId && displayVariants.length === 1 ? displayVariants[0] : null;

    // Title: when a single variant is pinned, append its label to the base title so the
    // card reflects the specified variant rather than the product's default-variant suffix.
    let productTitle;
    if (product.short_title) {
        productTitle = product.short_title;
    } else if (pinnedVariant) {
        const baseTitle = getProductBaseTitle(product);
        const rawLabel = pinnedVariant.option1 || pinnedVariant.title || '';
        const variantLabel = rawLabel && rawLabel !== 'Default Title' ? rawLabel : '';
        productTitle = variantLabel ? `${baseTitle} – ${variantLabel}` : baseTitle;
    } else {
        productTitle = product.title;
    }

    let variantSelectorHtml = '';
    if (hasVariants) {
        const optionName = product.options && product.options[0] ? product.options[0].name : '';
        const pills = displayVariants.map((v) => {
            const isFirst = v.id === (pinnedVariant ? pinnedVariant.id : product.variant_id);
            const isPurchasable = isVariantPurchasable(v);
            return `<button type="button" class="builder-variant-pill${isFirst ? ' -active' : ''}${isPurchasable ? '' : ' -unavailable'}" data-vid="${v.id}" data-vprice="${v.price}" data-vcompare="${v.compare_at_price || ''}" data-vavailable="${isPurchasable}" data-vimage="${v.featured_image || ''}" data-vlabel="${escapeHtml(v.option1)}" ${isPurchasable ? '' : 'disabled aria-disabled="true"'}>${escapeHtml(v.option1)}</button>`;
        }).join('');
        variantSelectorHtml = `<div class="builder-variant-selector" data-option-name="${escapeHtml(optionName)}">${pills}</div>`;
    }

    // Resolve effective values (use pinned variant if present)
    const effectiveVariantId = pinnedVariant ? pinnedVariant.id : product.variant_id;
    const effectivePrice = pinnedVariant ? pinnedVariant.price : product.price;
    const effectiveCompare = pinnedVariant ? (pinnedVariant.compare_at_price || null) : product.compare_at_price;
    const effectiveImage = (pinnedVariant && pinnedVariant.featured_image) ? pinnedVariant.featured_image : product.featured_image;
    const effectiveHasCompare = effectiveCompare && effectiveCompare !== effectivePrice;

    return `
        <li class="builder-product-card${hasVariants ? ' has-variants' : ''}">
            <input
                class="builder-product-input"
                type="${inputType}"
                id="${inputId}"
                name="${groupName}"
                data-product-handle="${escapeHtml(handle)}"
                data-variant-id="${effectiveVariantId}"
                data-free="${substep.free ? 'true' : 'false'}"
                data-step-index="${step.index}"
                data-substep-index="${substep.index}"
                data-summary-key="${summaryKey}"
            >
            <label for="${inputId}">
                <button type="button" class="builder-product-card__preview" data-preview-handle="${escapeHtml(handle)}" aria-label="${escapeHtml(getViewProductLabel())}">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="8" cy="8" r="7" stroke="currentColor" stroke-width="1.3"/><path d="M8 7v4.5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/><circle cx="8" cy="4.75" r="0.75" fill="currentColor"/></svg>
                </button>
                <figure>
                    <img src="${effectiveImage || '/cdn/shop/files/placeholder.jpg'}" alt="${escapeHtml(product.title)}">
                    ${preorderBadge}
                </figure>
                <div class="builder-product-card__content">
                    <h4>${escapeHtml(productTitle)}</h4>
                    ${variantSelectorHtml}
                    ${!substep.free ? `
                        <p class="builder-product-card__price-row">
                            <span class="price">${formatPrice(effectivePrice)}</span>
                            ${effectiveHasCompare ? `<span class="compare">${formatPrice(effectiveCompare)}</span>` : ''}
                        </p>
                    ` : ''}
                </div>
            </label>
        </li>
    `;
}

function renderProductCards(handles, step, substep, summaryKey, includeNoOptionCard, noOptionChecked) {
    const cards = [];

    if (includeNoOptionCard) {
        cards.push(renderNoOptionCard(step, substep, summaryKey, noOptionChecked));
    }

    normalizeProductList(handles).forEach((entry, index) => {
        const { handle, variantId } = parseHandleEntry(entry);
        cards.push(renderProductCard(handle, step, substep, index, summaryKey, variantId));
    });

    const filteredCards = cards.filter(Boolean);

    if (!filteredCards.length) {
        return `<div class="builder-product-list__empty">${escapeHtml(getUnavailableLabel())}</div>`;
    }

    return `<ul class="builder-product-grid">${filteredCards.join('')}</ul>`;
}

function renderCategoryNav(substep, activeCategoryIndex) {
    const navItems = substep.categories
        .map((category) => {
            const hasProducts = countAvailableProducts(category.product_list) > 0;
            const categoryTitle = category.category_title || noOptionLabel;

            return `
                <li
                    class="builder-category-nav__item${category.index === activeCategoryIndex ? ' -active' : ''}"
                    data-category-index="${category.index}"
                    data-has-products="${hasProducts}"
                >
                    <button type="button">${escapeHtml(categoryTitle)}</button>
                </li>
            `;
        })
        .join('');

    return `<nav class="builder-category-nav"><ul>${navItems}</ul></nav>`;
}

function renderSubstep(step, substep, startOpen) {
    const summaryKey = getSummaryKey(step.index, substep.index);

    BUILDER_STATE.substepLookup.set(summaryKey, {
        stepIndex: step.index,
        substepIndex: substep.index,
        stepTitle: step.title,
        substepTitle: substep.title,
        required: substep.required,
    });

    const hasCategories = substep.categories.length > 0;

    const availableOptionCount = hasCategories
        ? substep.categories.reduce((count, category) => count + countAvailableProducts(category.product_list), 0)
        : countAvailableProducts(substep.product_list);

    const autoSkip = substep.required && availableOptionCount === 0;

    const showHiddenNoOptionInput = !substep.multiselect && (autoSkip || !substep.required);
    const hiddenNoOptionInput = showHiddenNoOptionInput
        ? `
            <input
                class="builder-product-input builder-product-input--none"
                type="radio"
                id="${getNoOptionInputId(step.index, substep.index)}"
                name="${getInputGroupName(step.index, substep.index)}"
                data-no-product="true"
                data-step-index="${step.index}"
                data-substep-index="${substep.index}"
                data-summary-key="${summaryKey}"
                checked
            >
          `
        : '';

    let bodyContent = '';

    if (hasCategories) {
        const categoryWithProducts = substep.categories.find(
            (category) => countAvailableProducts(category.product_list) > 0
        );

        const activeCategoryIndex = categoryWithProducts
            ? categoryWithProducts.index
            : (substep.categories[0]?.index || 1);

        const fieldsets = substep.categories
            .map((category) => {
                const hasProducts = countAvailableProducts(category.product_list) > 0;
                const cards = hasProducts
                    ? renderProductCards(category.product_list, step, substep, summaryKey, false, false)
                    : `<div class="builder-product-list__empty">${escapeHtml(getUnavailableLabel())}</div>`;

                return `
                    <fieldset
                        class="builder-product-list${category.index === activeCategoryIndex ? '' : ' -hidden'}"
                        data-category-index="${category.index}"
                        data-has-products="${hasProducts}"
                    >
                        ${cards}
                    </fieldset>
                `;
            })
            .join('');

        const substepTextHtml = substep.text
            ? `<p class="builder-substep__text">${escapeHtml(substep.text)}${renderHelpLink(substep)}</p>`
            : (substep.help_content ? `<p class="builder-substep__text">${renderHelpLink(substep)}</p>` : '');

        bodyContent = `
            ${substepTextHtml}
            ${renderCategoryNav(substep, activeCategoryIndex)}
            ${hiddenNoOptionInput}
            ${fieldsets}
        `;
    } else {
        const cards = renderProductCards(
            substep.product_list,
            step,
            substep,
            summaryKey,
            false,
            false
        );

        const substepTextHtml = substep.text
            ? `<p class="builder-substep__text">${escapeHtml(substep.text)}${renderHelpLink(substep)}</p>`
            : (substep.help_content ? `<p class="builder-substep__text">${renderHelpLink(substep)}</p>` : '');

        bodyContent = `
            ${substepTextHtml}
            ${hiddenNoOptionInput}
            <fieldset class="builder-product-list" data-has-products="${availableOptionCount > 0}">
                ${cards}
            </fieldset>
        `;
    }

    const noProducts = availableOptionCount === 0;
    const substepClasses = ['builder-substep'];
    if (startOpen && !noProducts) substepClasses.push('-open');
    if (noProducts) substepClasses.push('-no-products');

    return `
        <section
            class="${substepClasses.join(' ')}"
            data-step-index="${step.index}"
            data-substep-index="${substep.index}"
            data-summary-key="${summaryKey}"
            data-required="${substep.required}"
            data-multiselect="${!!substep.multiselect}"
            data-autoskip="${autoSkip}"
        >
            ${renderSubstepHeader(substep)}
            <div class="builder-substep__body">
                ${bodyContent}
            </div>
        </section>
    `;
}

function renderBuilderSteps(builderStructure) {
    const container = document.getElementById('builder-container');
    if (!container || !builderStructure?.steps?.length) return;

    // Remove previously rendered steps while keeping the title node.
    container.querySelectorAll('article.step').forEach((node) => node.remove());

    BUILDER_STATE.substepLookup.clear();

    builderStructure.steps.forEach((step) => {
        const substepsHtml = step.substeps
            .map((substep, substepPosition) =>
                renderSubstep(step, substep, substepPosition === 0))
            .join('');

        const stepMarkup = `
            <article class="step -open" data-step-index="${step.index}">
                ${renderStepHeader(step)}
                <div class="builder-step__body">
                    ${substepsHtml}
                </div>
            </article>
        `;

        container.insertAdjacentHTML('beforeend', stepMarkup);
    });

    // Hide steps where every substep has no products
    container.querySelectorAll('article.step').forEach((stepElem) => {
        const substeps = stepElem.querySelectorAll('.builder-substep');
        const allEmpty = substeps.length > 0 && Array.from(substeps).every((s) => s.classList.contains('-no-products'));
        if (allEmpty) stepElem.classList.add('-no-products');
    });
}

function renderPromos(builderStructure) {
    const container = document.querySelector('.builder-promos');
    if (!container) return;

    const cardsList = container.querySelector('.builder-promo__cards');
    if (!cardsList) return;
    cardsList.innerHTML = '';

    const promos = builderStructure?.promos;
    if (!Array.isArray(promos) || !promos.length) {
        container.classList.add('hidden');
        return;
    }

    let hasRendered = false;

    promos.forEach((promo, i) => {
        const product = getAvailableProduct(promo.handle);
        if (!product) return;

        hasRendered = true;
        const isFree = promo.discount === 100;
        const hasDiscount = promo.discount > 0 && promo.discount < 100;
        const isLocked = !promo.can_unselect;
        const allVariants = product.variants && product.variants.length > 1 ? product.variants : [];
        const hasVariants = allVariants.length > 1;
        const defaultVariant = allVariants.length ? getProductVariant(product, product.variant_id) : null;
        const originalPrice = parseInt((defaultVariant ? defaultVariant.price : product.price) || 0, 10);
        const compareAtPrice = parseInt((defaultVariant ? defaultVariant.compare_at_price : product.compare_at_price) || 0, 10);
        const hasSale = !hasDiscount && !isFree && compareAtPrice > originalPrice;
        const discountedPrice = isFree ? 0 : (hasDiscount ? Math.round(originalPrice * (1 - promo.discount / 100)) : originalPrice);
        const inputId = `promo-${product.id}-${i}`;
        let productTitle = product.short_title || product.title;
        // Strip variant suffix from title when pills are shown (e.g. "Product – Blanc" → "Product")
        if (hasVariants && !product.short_title) {
            const defaultLabel = allVariants.find((v) => v.id === product.variant_id);
            if (defaultLabel) {
                productTitle = productTitle.replace(new RegExp('\\s*[–\\-]\\s*' + defaultLabel.option1.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '$'), '');
            }
        }

        let variantSelectorHtml = '';
        if (hasVariants) {
            const optionName = product.options && product.options[0] ? product.options[0].name : '';
            const pills = allVariants.map((v) => {
                const isFirst = v.id === product.variant_id;
                const isPurchasable = isVariantPurchasable(v);
                return `<button type="button" class="builder-variant-pill${isFirst ? ' -active' : ''}${isPurchasable ? '' : ' -unavailable'}" data-vid="${v.id}" data-vprice="${v.price}" data-vcompare="${v.compare_at_price || ''}" data-vavailable="${isPurchasable}" data-vimage="${v.featured_image || ''}" data-vlabel="${escapeHtml(v.option1)}" ${isPurchasable ? '' : 'disabled aria-disabled="true"'}>${escapeHtml(v.option1)}</button>`;
            }).join('');
            variantSelectorHtml = `<div class="builder-variant-selector" data-option-name="${escapeHtml(optionName)}">${pills}</div>`;
        }

        let priceHtml;
        if (isFree) {
            priceHtml = `<div class="builder-promo__price">
                   <s class="price-price">${formatPrice(originalPrice)}</s>
                   <span class="price-free">${escapeHtml(freeLabel)}</span>
               </div>`;
        } else if (hasDiscount) {
            priceHtml = `<div class="builder-promo__price">
                   <s class="price-price">${formatPrice(originalPrice)}</s>
                   <span class="price-discounted">${formatPrice(discountedPrice)}</span>
               </div>
               <span class="builder-promo__badge">-${promo.discount}%</span>`;
        } else if (hasSale) {
            priceHtml = `<div class="builder-promo__price">
                   <s class="price-price">${formatPrice(compareAtPrice)}</s>
                   <span class="price-discounted">${formatPrice(originalPrice)}</span>
               </div>`;
        } else {
            priceHtml = `<div class="builder-promo__price">
                   <span class="price-discounted">${formatPrice(originalPrice)}</span>
               </div>`;
        }

        const preorderHtml = isPreorder(product) ? `<div class="builder-promo__message builder-promo__message--preorder">${escapeHtml(getPreorderLabel())}</div>` : '';

        const card = `
            <div class="builder-promo__card">
                <input
                    class="builder-product-input builder-promo-input"
                    type="checkbox"
                    id="${inputId}"
                    data-product-handle="${escapeHtml(promo.handle)}"
                    data-variant-id="${product.variant_id}"
                    data-promo="true"
                    data-discount="${promo.discount}"
                    ${isFree ? 'data-free="true"' : ''}
                    ${isLocked ? 'disabled' : ''}
                    ${isFree || isLocked ? 'checked' : ''}
                >
                <div class="builder-promo__toggle">
                    <div class="rd-checkbox">
                        <label class="rd-checkbox__label" for="${inputId}"></label>
                    </div>
                </div>
                <div class="builder-promo__image">
                    <img src="${product.featured_image || fallbackImage}" alt="${escapeHtml(product.title)}" loading="lazy">
                    <button type="button" class="builder-promo__preview" data-preview-handle="${escapeHtml(promo.handle)}" aria-label="${escapeHtml(getViewProductLabel())}">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="8" cy="8" r="7" stroke="currentColor" stroke-width="1.3"/><path d="M8 7v4.5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/><circle cx="8" cy="4.75" r="0.75" fill="currentColor"/></svg>
                    </button>
                    ${preorderHtml}
                </div>
                <div class="builder-promo__info">
                    <span class="builder-promo__title">${escapeHtml(productTitle)}</span>
                    ${variantSelectorHtml}
                    ${priceHtml}
                </div>
            </div>
        `;

        cardsList.insertAdjacentHTML('beforeend', card);
    });

    container.classList.toggle('hidden', !hasRendered);

    // Wire up checkbox change events
    container.querySelectorAll('.builder-promo-input').forEach((input) => {
        input.addEventListener('change', () => {
            notifySelectionChanged();
        });
    });
}

//-----------------------------
// Interaction Logic
//-----------------------------

function activateCategory(substepElem, categoryIndex, shouldAdvance) {
    if (!substepElem) return;

    const navItems = substepElem.querySelectorAll('.builder-category-nav__item');
    const fieldsets = substepElem.querySelectorAll('.builder-product-list[data-category-index]');

    navItems.forEach((item) => {
        item.classList.toggle('-active', ensureNumber(item.dataset.categoryIndex, -1) === categoryIndex);
    });

    let activeFieldset = null;

    fieldsets.forEach((fieldset) => {
        const isActive = ensureNumber(fieldset.dataset.categoryIndex, -1) === categoryIndex;
        fieldset.classList.toggle('-hidden', !isActive);
        if (isActive) activeFieldset = fieldset;
    });

    const hasProducts = activeFieldset?.dataset.hasProducts === 'true';
    const hiddenNoOptionInput = substepElem.querySelector('.builder-product-input--none[data-no-product="true"]');

    if (!hasProducts) {
        const productInputs = substepElem.querySelectorAll('.builder-product-input:not([data-no-product="true"])');
        productInputs.forEach((input) => {
            input.checked = false;
        });

        if (hiddenNoOptionInput) {
            hiddenNoOptionInput.checked = true;
            notifySelectionChanged();
            if (shouldAdvance) openNextSubstep(hiddenNoOptionInput);
        } else {
            notifySelectionChanged();
        }

        return;
    }

    if (hiddenNoOptionInput && hiddenNoOptionInput.checked) {
        hiddenNoOptionInput.checked = false;
        notifySelectionChanged();
    }
}

function handleSelectionChange(input, options = {}) {
    const substepElem = input.closest('.builder-substep');
    const isMultiselect = substepElem && substepElem.dataset.multiselect === 'true';
    const shouldAdvance = options.advance !== false && !isMultiselect;

    notifySelectionChanged();

    if (shouldAdvance) {
        openNextSubstep(input);
    }
}

function initBuilder() {
    // Substep accordion
    document.querySelectorAll('.pool-builder .builder-substep__header').forEach((header) => {
        header.addEventListener('click', () => {
            const substepElem = header.closest('.builder-substep');
            if (!substepElem) return;
            toggleSubstep(substepElem);
        });
    });

    // Category tabs
    document.querySelectorAll('.pool-builder .builder-category-nav__item').forEach((item) => {
        item.addEventListener('click', () => {
            const substepElem = item.closest('.builder-substep');
            const categoryIndex = ensureNumber(item.dataset.categoryIndex, 1);
            activateCategory(substepElem, categoryIndex, true);
        });
    });

    // Product selections
    document.querySelectorAll('.pool-builder .builder-product-input').forEach((input) => {
        input.addEventListener('change', (event) => {
            handleSelectionChange(event.target);
        });
    });

    // Optional single-select cards: clicking an already selected item clears it.
    document.querySelector('.pool-builder')?.addEventListener('click', (event) => {
        const label = event.target.closest('.builder-product-card label');
        if (!label) return;
        if (event.target.closest('.builder-product-card__preview')) return;
        if (event.target.closest('.builder-variant-pill')) return;

        const inputId = label.getAttribute('for');
        if (!inputId) return;

        const input = document.getElementById(inputId);
        if (!input || input.type !== 'radio' || input.dataset.noProduct === 'true') return;

        const substepElem = input.closest('.builder-substep');
        const isOptionalSingleSelect =
            substepElem &&
            substepElem.dataset.required !== 'true' &&
            substepElem.dataset.multiselect !== 'true';

        if (!isOptionalSingleSelect || !input.checked) return;

        event.preventDefault();

        const noneInput = substepElem.querySelector('.builder-product-input--none[data-no-product="true"]');
        if (!noneInput) return;

        input.checked = false;
        noneInput.checked = true;
        notifySelectionChanged();
    });

    // Variant pill selection (event delegation)
    document.querySelector('.builder-wrapper')?.addEventListener('click', (event) => {
        const pill = event.target.closest('.builder-variant-pill');
        if (!pill) return;

        if (pill.disabled || pill.dataset.vavailable !== 'true') return;

        // Prevent the click from toggling the parent radio/checkbox
        event.preventDefault();
        event.stopPropagation();

        const selector = pill.closest('.builder-variant-selector');
        if (!selector) return;

        // Update active pill
        selector.querySelectorAll('.builder-variant-pill').forEach((p) => p.classList.remove('-active'));
        pill.classList.add('-active');

        // Find the card's hidden input and update variant_id
        const card = pill.closest('.builder-product-card') || pill.closest('.builder-promo__card');
        if (!card) return;
        const input = card.querySelector('.builder-product-input');
        if (input) {
            input.dataset.variantId = pill.dataset.vid;
        }

        // Update price display on the card (product cards)
        const priceRow = card.querySelector('.builder-product-card__price-row');
        if (priceRow) {
            const priceEl = priceRow.querySelector('.price');
            const compareEl = priceRow.querySelector('.compare');
            if (priceEl) priceEl.textContent = formatPrice(pill.dataset.vprice);
            if (pill.dataset.vcompare && pill.dataset.vcompare !== pill.dataset.vprice) {
                if (compareEl) {
                    compareEl.textContent = formatPrice(pill.dataset.vcompare);
                    compareEl.hidden = false;
                } else {
                    priceEl.insertAdjacentHTML('afterend', `<span class="compare">${formatPrice(pill.dataset.vcompare)}</span>`);
                }
            } else if (compareEl) {
                compareEl.hidden = true;
            }
        }

        // Update price display on promos (non-free only)
        const promoPrice = card.querySelector('.builder-promo__price');
        if (promoPrice) {
            const strikeEl = promoPrice.querySelector('.price-price');
            const discountEl = promoPrice.querySelector('.price-discounted');
            const discount = input ? parseInt(input.dataset.discount || '0', 10) : 0;
            const variantPrice = parseInt(pill.dataset.vprice || '0', 10);
            const variantCompare = parseInt(pill.dataset.vcompare || '0', 10);
            if (discount > 0 && discount < 100) {
                if (strikeEl) strikeEl.textContent = formatPrice(variantPrice);
                if (discountEl) discountEl.textContent = formatPrice(Math.round(variantPrice * (1 - discount / 100)));
            } else if (discount === 0 && variantCompare > variantPrice) {
                if (strikeEl) strikeEl.textContent = formatPrice(variantCompare);
                if (discountEl) discountEl.textContent = formatPrice(variantPrice);
            } else if (discount === 0) {
                if (strikeEl) strikeEl.remove();
                if (discountEl) discountEl.textContent = formatPrice(variantPrice);
            } else if (strikeEl) {
                strikeEl.textContent = formatPrice(variantPrice);
            }
        }

        // Update card image if variant has its own
        if (pill.dataset.vimage) {
            const img = card.querySelector('figure img') || card.querySelector('.builder-promo__image img');
            if (img) img.src = pill.dataset.vimage;
        }

        // Refresh summary + totals if this card is checked
        if (input && input.checked) {
            notifySelectionChanged();
        }
    });

    // Summary modify links
    const summaryContainer = document.querySelector('.builder-summary');
    if (summaryContainer) {
        summaryContainer.addEventListener('click', (event) => {
            const trigger = event.target.closest('[data-goto-substep]');
            if (!trigger) return;

            goToSubstep(trigger.dataset.gotoSubstep);
        });
    }

    // Selections toggle (collapsible body)
    const selectionsToggle = document.querySelector('[data-selections-toggle]');
    if (selectionsToggle) {
        selectionsToggle.addEventListener('click', () => {
            const isOpen = selectionsToggle.classList.toggle('-open');
            selectionsToggle.setAttribute('aria-expanded', String(isOpen));
        });
    }

    // Help modal openers
    document.addEventListener('click', (event) => {
        // Product preview
        const previewBtn = event.target.closest('[data-preview-handle]');
        if (previewBtn) {
            event.preventDefault();
            event.stopPropagation();
            const card = previewBtn.closest('.builder-product-card, .builder-promo__card');
            const input = card?.querySelector('.builder-product-input');
            openProductPreview(previewBtn.dataset.previewHandle, input?.dataset.variantId || null);
            return;
        }

        const previewCloser = event.target.closest('.js-close-preview');
        if (previewCloser) {
            closeProductPreview(previewCloser);
            return;
        }

        // Help modal
        const opener = event.target.closest('[data-open-help]');
        if (opener) {
            event.preventDefault();
            openHelpModal(opener.dataset.openHelp);
            return;
        }

        const closer = event.target.closest('.js-close-help');
        if (closer) {
            closeHelpModal(closer);
        }
    });

    document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
            const previewModal = document.querySelector('.builder-preview-modal');
            if (previewModal) {
                previewModal.remove();
                if (!document.querySelector('.builder-help-modal.-open')) {
                    document.body.style.overflow = '';
                }
                return;
            }
            const openModal = document.querySelector('.builder-help-modal.-open');
            if (openModal) {
                openModal.classList.remove('-open');
                document.body.style.overflow = '';
            }
        }
    });
}

//-----------------------------
// Countdown (weekly reset)
//-----------------------------

function updateCountdown() {
    const el = document.getElementById('promo-countdown');
    if (!el) return;

    const now = new Date();
    const msInDay = 24 * 60 * 60 * 1000;
    const msInWeek = 7 * msInDay;

    // Fixed reference Monday
    const reference = new Date(2025, 0, 6, 0, 0, 0);
    const elapsed = now - reference;
    const cycleStart = new Date(reference.getTime() + Math.floor(elapsed / msInWeek) * msInWeek);
    const nextCycle = new Date(cycleStart.getTime() + msInWeek);
    const remaining = nextCycle - now;

    if (remaining <= 0) {
        el.textContent = '';
        return;
    }

    const days = Math.floor(remaining / msInDay);
    const hours = Math.floor((remaining % msInDay) / (60 * 60 * 1000));
    const minutes = Math.floor((remaining % (60 * 60 * 1000)) / (60 * 1000));
    const seconds = Math.floor((remaining % (60 * 1000)) / 1000);

    const lang = document.documentElement.lang || 'en';
    const dayLabel = lang.startsWith('fr') ? (days === 1 ? 'jour' : 'jours') : (days === 1 ? 'day' : 'days');
    const remainingLabel = lang.startsWith('fr') ? 'restant' : 'remaining';

    el.innerHTML = `<span class="separator"></span><span class="builder-promos__timer-text">${days} ${dayLabel} ${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')} ${remainingLabel}</span><span class="separator"></span>`;
}

//-----------------------------
// Initialization
//-----------------------------

document.addEventListener('DOMContentLoaded', () => {
    BUILDER_STATE.structure = normalizeBuilderStructure(window.builderStructure || {});

    renderBuilderSteps(BUILDER_STATE.structure);
    renderPromos(BUILDER_STATE.structure);
    renderHelpModals(BUILDER_STATE.structure);
    initBuilder();
    enforceInitialOpenState();
    restoreSelections();

    updateCountdown();
    setInterval(updateCountdown, 1000);

    document.addEventListener('builder:selectionChanged', () => {
        updateTotalPrice();
        setStickyTitle();
        renderBuilderSummary();
        updateBuilderProgress();
        updateStepCounters();
        updateSubstepCounters();
        saveSelections();
    });

    // Trigger initial paint for summary, totals and progress.
    notifySelectionChanged();
});
